import React, { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Loader } from '../../../ui_components/loader';
import { changeCard, changeSubscriptionPlan } from '../../../api/account';
import { PRICING_TABLE_PLANS_GATE } from '../../../pages/team_settings';
import { checkGate } from '../../../statsig';
import { CancellationModal } from '../../../modals/cancellation-modal';
import { cancelSubscription } from '../../../api/stripe';
import moment from 'moment';
import { BillingItem } from '../billing_grid_row';
import { BillingHistoryModal } from '../../../modals/billing_history';
import { Config } from '../../../utils';

type QuipaSubscriptionProps = {
  activeTab: boolean;
  loading: boolean;
  disabled: boolean;
  accountId: any;
  seatUsageAvailable: number;
  showCancelMembership: boolean;
  showBuyMoreOrders: boolean;
  currentPlanInfo: any;
  planInterval: any;
  seatUsageUsed: number;
  seatUsagePending: number;
  errorOrderLimit: boolean;
  planLimitInfo: { orderLimit: number | undefined; orderLimitLeft: number | undefined };
  remainingItemOrder: number;
  extraItemOrder: number;
  planLimitLoading: boolean;
  last4: string;
  historyItems: BillingItem[];
  attachResizeObserver(): void;
  scrollToSection(opts?: { smooth?: boolean; headerOffset?: number }): void;
  customerSecret: string | null;
  sectionRef: any;
  hostRef: any;
};

const QuipaSubscription = (props: QuipaSubscriptionProps) => {
  const {
    disabled = false,
    accountId,
    activeTab,
    loading,
    seatUsageAvailable = 0,
    showCancelMembership = false,
    showBuyMoreOrders = false,
    currentPlanInfo,
    planInterval,
    seatUsageUsed = 0,
    seatUsagePending = 0,
    errorOrderLimit = false,
    planLimitInfo = { orderLimit: undefined, orderLimitLeft: undefined },
    remainingItemOrder = false,
    extraItemOrder = 0,
    planLimitLoading = false,
    last4 = '',
    historyItems = [],
    attachResizeObserver,
    scrollToSection,
    customerSecret = null,
    sectionRef = null,
    hostRef = null,
  } = props;

  const [openCancelMembershipModal, setOpenCancelMembershipModal] = useState<boolean>(false);
  const [isCancellationMembership, setIsCancellationMembership] = useState<boolean>(false);
  const [billingHistoryModalActive, setBillingHistoryModalActive] = useState<boolean>(false);
  const [buyMoreOrders, setBuyMoreOrders] = useState<boolean>(false);

  useEffect(() => {
    if (buyMoreOrders) {
      attachResizeObserver();
      scrollToSection({ smooth: true });
    }
  }, [buyMoreOrders]);

  const handleOpenCancelMembershipModal = (val: boolean) => {
    setOpenCancelMembershipModal(val);
  };

  const cancelMembership = async (): Promise<void> => {
    const res = await cancelSubscription(accountId);
    if (res.status === 'canceled') {
      setIsCancellationMembership(true);
      handleOpenCancelMembershipModal(false);
    }
  };

  const handleConfirmCancellation = () => {
    cancelMembership();
  };

  const changeCreditCard = async (): Promise<void> => {
    const session = await changeCard(accountId);
    window.location.href = session.url;
  };

  const changeQuipaPlan = async (): Promise<void> => {
    const hasSeatsPlan = seatUsageAvailable > 0;
    const useNewBillingPortal = hasSeatsPlan || checkGate(PRICING_TABLE_PLANS_GATE);
    const variant = useNewBillingPortal ? 'new' : 'old';
    const session = await changeSubscriptionPlan(accountId, variant);
    window.location.href = session.url;
  };

  const handleOpenHistoryPaymentModal = (val: boolean) => {
    setBillingHistoryModalActive(val);
  };

  const handleBuyMoreOrders = async (): Promise<void> => {
    setBuyMoreOrders((prev) => !prev);
  };

  const remaining_orders_left =
    planLimitInfo &&
    planLimitInfo.orderLimit !== undefined &&
    planLimitInfo.orderLimitLeft !== undefined
      ? planLimitInfo.orderLimit - planLimitInfo.orderLimitLeft
      : undefined;
  const isCanceled = currentPlanInfo && currentPlanInfo.cancelAtPeriodEnd ? true : false;
  const planPrice =
    ((currentPlanInfo?.monthlyPrice || 0) - (currentPlanInfo?.discountAmount || 0)) / 100;

  return (
    <div className={`flex-1 min-w-0 space-y-6 ${activeTab ? 'block' : 'hidden'}`}>
      <div className="space-y-6">
        <div className="space-y-4 p-[16px] bg-muted/50 rounded-lg">
          <h3 className="m-0 text-[16px] font-semibold text-foreground">QUIPA Subscription</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-sm text-foreground m-0">
                Account ID: <span className="font-semibold">{accountId}</span>
              </p>

              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground m-0">Current Plan</p>
                <p className="text-sm text-foreground">{currentPlanInfo?.planName || ''}</p>
                <p className="text-sm text-foreground">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(planPrice)}{' '}
                  {planInterval === 'month' ? 'monthly' : 'annually'}
                </p>
                <p className="text-sm text-foreground">
                  {isCanceled || isCancellationMembership ? 'Membership Ends' : 'Renewal Date'}:{' '}
                  {currentPlanInfo && currentPlanInfo.renewalDate
                    ? moment(currentPlanInfo.renewalDate).format('MMMM DD, YYYY')
                    : ''}
                </p>
                {seatUsageAvailable > 0 && (
                  <p className="text-sm text-foreground">
                    {seatUsageUsed} of {seatUsageAvailable} seats used
                    {seatUsagePending > 0 ? ` (includes ${seatUsagePending} pending)` : ''}
                  </p>
                )}
                <p className="text-sm text-foreground">
                  <span>{`Item Orders (${
                    errorOrderLimit
                      ? 'Invalid duration'
                      : planInterval === 'month'
                        ? `${planInterval}ly`
                        : 'annually'
                  }): `}</span>
                  <span>
                    {errorOrderLimit
                      ? 'Invalid remaining'
                      : planLimitInfo && planLimitInfo.orderLimit === undefined
                        ? 'Unlimited'
                        : `${
                            remaining_orders_left && remaining_orders_left < 0
                              ? 0
                              : remaining_orders_left
                          } of ${planLimitInfo.orderLimit} remaining`}
                  </span>
                </p>
                <p className="text-sm text-foreground">
                  {!planLimitLoading && planLimitInfo && planLimitInfo.orderLimit !== undefined && (
                    <span>{`Extra Item Orders: ${
                      remainingItemOrder !== undefined ? remainingItemOrder : 0
                    } of ${extraItemOrder} remaining`}</span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {showBuyMoreOrders && (
                  <Button
                    data-qa={`subscription_buy_more_orders_button`}
                    disabled={disabled}
                    onClick={handleBuyMoreOrders}
                    variant={'default'}
                    type="button"
                    className="font-medium"
                  >
                    Buy More Orders
                  </Button>
                )}
                <Button
                  data-qa={`subscription_change_plan_button`}
                  disabled={disabled}
                  onClick={changeQuipaPlan}
                  variant={'outline'}
                  type="button"
                  className="font-medium"
                >
                  Change Plan
                </Button>
                {!isCancellationMembership && showCancelMembership && (
                  <Button
                    data-qa={`subscription_cancel_membership`}
                    disabled={disabled}
                    onClick={() => handleOpenCancelMembershipModal(true)}
                    variant={'link'}
                    type="button"
                    className="text-muted-foreground hover:text-foreground font-medium"
                  >
                    Cancel Membership
                  </Button>
                )}
              </div>

              {buyMoreOrders && customerSecret && (
                <div ref={sectionRef} style={{ scrollMarginTop: 80 }}>
                  <stripe-pricing-table
                    ref={hostRef}
                    pricing-table-id={Config.pricingTableBuyMoreOrdersId || null}
                    publishable-key={Config.stripeBuyMoreOrdersPk}
                    client-reference-id={accountId}
                    customer-session-client-secret={customerSecret}
                  ></stripe-pricing-table>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground m-0">
                Current Card {`x${last4}`}
              </p>
              <Button
                data-qa={`subscription_change_card`}
                disabled={disabled}
                onClick={changeCreditCard}
                variant={'outline'}
                type="button"
                className="font-medium"
              >
                Change Card
              </Button>

              <div className="pt-4">
                <p className="text-sm font-semibold text-foreground mb-2">Payment History</p>
                {historyItems?.slice(0, 6)?.map((item, index) => {
                  return (
                    <div key={index}>
                      <a data-qa={`subscription_view_payment_${index}`} href={item.receiptUrl} className="text-sm text-primary">
                        {moment(item.date).format('DD MMMM YYYY')}
                      </a>
                    </div>
                  );
                })}

                {historyItems.length > 6 && (
                  <div className="mt-[10px]">
                    <Button data-qa={`subscription_view_more_payment_history`} variant={'default'} onClick={() => handleOpenHistoryPaymentModal(true)}>
                      View more
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CancellationModal
        active={openCancelMembershipModal}
        onRequestClose={() => handleOpenCancelMembershipModal(false)}
        handleConfirmCancellation={handleConfirmCancellation}
        accountId={accountId}
      />

      <BillingHistoryModal
        onRequestClose={() => handleOpenHistoryPaymentModal(false)}
        active={billingHistoryModalActive}
        items={historyItems}
      />

      {loading && <Loader className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2" />}
    </div>
  );
};

export default QuipaSubscription;
